import React, { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';

const PlayerPerformanceTrends = ({ allPlays, boxScore, awayAbbr, homeAbbr }) => {
  const [selectedTeam, setSelectedTeam] = useState('both');

  const { topBatters, pitchers } = useMemo(() => {
    if (!boxScore) return { topBatters: [], pitchers: [] };
    const batters = [];
    const pits = [];

    for (const side of ['away', 'home']) {
      const abbr = side === 'away' ? awayAbbr : homeAbbr;
      const players = Object.values(boxScore.teams?.[side]?.players || {});
      players.forEach((p) => {
        const b = p.stats?.batting;
        const pit = p.stats?.pitching;
        const name = p.person?.fullName || '--';
        const last = name.split(' ').slice(1).join(' ') || name;
        if (b && b.atBats > 0) {
          batters.push({ name: last, side, abbr, ab: b.atBats, h: b.hits, hr: b.homeRuns || 0, rbi: b.rbi || 0, r: b.runs || 0, bb: b.baseOnBalls || 0, k: b.strikeOuts || 0, avg: b.atBats > 0 ? (b.hits / b.atBats) : 0 });
        }
        if (pit && parseFloat(pit.inningsPitched) > 0) {
          pits.push({ name: last, side, abbr, ip: parseFloat(pit.inningsPitched), k: pit.strikeOuts || 0, er: pit.earnedRuns || 0, bb: pit.baseOnBalls || 0, h: pit.hits || 0, p: pit.pitchesThrown || 0 });
        }
      });
    }

    batters.sort((a, b) => (b.h * 2 + b.hr * 4 + b.rbi * 2) - (a.h * 2 + a.hr * 4 + a.rbi * 2));
    pits.sort((a, b) => b.ip - a.ip);
    return { topBatters: batters.slice(0, 8), pitchers: pits.slice(0, 6) };
  }, [boxScore, awayAbbr, homeAbbr]);

  const filtered = useMemo(() => {
    if (selectedTeam === 'both') return { batters: topBatters, pitchers };
    const side = selectedTeam === awayAbbr ? 'away' : 'home';
    return { batters: topBatters.filter(p => p.side === side), pitchers: pitchers.filter(p => p.side === side) };
  }, [selectedTeam, topBatters, pitchers, awayAbbr, homeAbbr]);

  const batterChartOption = useMemo(() => {
    const names = filtered.batters.map(p => p.name);
    return {
      backgroundColor: 'transparent',
      grid: { top: 20, right: 20, bottom: 60, left: 60 },
      legend: { bottom: 0, textStyle: { color: '#a9c9b1', fontSize: 9 }, itemWidth: 10, itemHeight: 8 },
      tooltip: { trigger: 'axis', backgroundColor: '#1e3a29', borderColor: '#658d71', textStyle: { color: '#d8ceb3', fontSize: 10 } },
      xAxis: { type: 'category', data: names, axisLabel: { color: '#a9c9b1', fontSize: 8, rotate: 30 }, axisLine: { lineStyle: { color: '#658d71' } } },
      yAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#a9c9b1', fontSize: 9 }, axisLine: { lineStyle: { color: '#658d71' } }, splitLine: { lineStyle: { color: 'rgba(101,141,113,0.2)' } } },
      series: [
        { name: 'Hits', type: 'bar', data: filtered.batters.map(p => p.h), itemStyle: { color: 'rgba(169,201,177,0.85)', borderColor: '#a9c9b1', borderWidth: 1 }, stack: 'batting' },
        { name: 'HR', type: 'bar', data: filtered.batters.map(p => p.hr), itemStyle: { color: 'rgba(196,51,62,0.85)', borderColor: '#c4333e', borderWidth: 1 }, stack: 'batting' },
        { name: 'RBI', type: 'bar', data: filtered.batters.map(p => p.rbi), itemStyle: { color: 'rgba(216,206,179,0.7)', borderColor: '#d8ceb3', borderWidth: 1 }, stack: 'batting' },
      ],
    };
  }, [filtered.batters]);

  const pitcherChartOption = useMemo(() => {
    const names = filtered.pitchers.map(p => p.name);
    return {
      backgroundColor: 'transparent',
      grid: { top: 20, right: 20, bottom: 60, left: 44 },
      legend: { bottom: 0, textStyle: { color: '#a9c9b1', fontSize: 9 }, itemWidth: 10, itemHeight: 8 },
      tooltip: { trigger: 'axis', backgroundColor: '#1e3a29', borderColor: '#658d71', textStyle: { color: '#d8ceb3', fontSize: 10 } },
      xAxis: { type: 'category', data: names, axisLabel: { color: '#a9c9b1', fontSize: 8, rotate: 30 }, axisLine: { lineStyle: { color: '#658d71' } } },
      yAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#a9c9b1', fontSize: 9 }, axisLine: { lineStyle: { color: '#658d71' } }, splitLine: { lineStyle: { color: 'rgba(101,141,113,0.2)' } } },
      series: [
        { name: 'K', type: 'bar', data: filtered.pitchers.map(p => p.k), itemStyle: { color: 'rgba(169,201,177,0.85)', borderColor: '#a9c9b1', borderWidth: 1 } },
        { name: 'ER', type: 'bar', data: filtered.pitchers.map(p => p.er), itemStyle: { color: 'rgba(196,51,62,0.8)', borderColor: '#c4333e', borderWidth: 1 } },
        { name: 'BB', type: 'bar', data: filtered.pitchers.map(p => p.bb), itemStyle: { color: 'rgba(216,206,179,0.6)', borderColor: '#d8ceb3', borderWidth: 1 } },
      ],
    };
  }, [filtered.pitchers]);

  if (!boxScore) return (
    <div className="flex items-center justify-center h-40 text-[#658d71] text-xs tracking-widest uppercase">No player data available</div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Team Filter */}
      <div className="flex gap-1.5 flex-wrap">
        {['both', awayAbbr, homeAbbr].map((t) => (
          <button key={t} onClick={() => setSelectedTeam(t)}
            className={`text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 border transition-colors ${selectedTeam === t ? 'bg-[#658d71] border-[#a9c9b1] text-white' : 'border-[#658d71] text-[#a9c9b1] hover:text-white'}`}>
            {t === 'both' ? 'Both Teams' : t}
          </button>
        ))}
      </div>

      {/* Batting Chart */}
      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Batter Performance</span>
          <p className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest mt-0.5">Hits · Home Runs · RBI by player</p>
        </div>
        <div className="p-2">
          {filtered.batters.length > 0
            ? <ReactECharts option={batterChartOption} style={{ height: '190px' }} />
            : <div className="flex items-center justify-center h-20 text-[#658d71] text-xs uppercase tracking-widest">No batter data</div>}
        </div>
      </div>

      {/* Batter Stat Table */}
      {filtered.batters.length > 0 && (
        <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)] overflow-x-auto">
          <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
            <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Batting Lines</span>
          </div>
          <table className="w-full text-center" style={{ minWidth: '420px' }}>
            <thead>
              <tr className="border-b border-[#658d71] text-[#658d71] text-[8px] uppercase tracking-widest">
                <th className="px-2 py-1.5 text-left">Player</th>
                {['AB','H','HR','RBI','R','BB','K','AVG'].map(h => <th key={h} className="px-1 py-1.5 w-8">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.batters.map((p, i) => (
                <tr key={i} className="border-b border-[rgba(101,141,113,0.2)] last:border-0">
                  <td className="px-2 py-1.5 text-left">
                    <span className="text-[10px] font-black text-white uppercase tracking-wide">{p.name}</span>
                    <span className="ml-1.5 text-[8px] font-bold" style={{ color: p.side === 'away' ? '#a9c9b1' : '#c4333e' }}>{p.abbr}</span>
                  </td>
                  {[p.ab, p.h, p.hr || '–', p.rbi || '–', p.r || '–', p.bb || '–', p.k || '–'].map((v, j) => (
                    <td key={j} className="px-1 py-1.5 text-[10px] tabular-nums text-[#a9c9b1] font-bold">{v}</td>
                  ))}
                  <td className="px-1 py-1.5 text-[10px] tabular-nums text-[#d8ceb3] font-black">
                    {p.avg > 0 ? p.avg.toFixed(3).replace('0.', '.') : '.000'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pitching Chart */}
      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Pitcher Performance</span>
          <p className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest mt-0.5">Strikeouts · Earned Runs · Walks by pitcher</p>
        </div>
        <div className="p-2">
          {filtered.pitchers.length > 0
            ? <ReactECharts option={pitcherChartOption} style={{ height: '190px' }} />
            : <div className="flex items-center justify-center h-20 text-[#658d71] text-xs uppercase tracking-widest">No pitcher data</div>}
        </div>
      </div>

      {/* Pitcher Stat Table */}
      {filtered.pitchers.length > 0 && (
        <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)] overflow-x-auto">
          <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
            <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Pitching Lines</span>
          </div>
          <table className="w-full text-center" style={{ minWidth: '380px' }}>
            <thead>
              <tr className="border-b border-[#658d71] text-[#658d71] text-[8px] uppercase tracking-widest">
                <th className="px-2 py-1.5 text-left">Pitcher</th>
                {['IP','K','ER','BB','H','P'].map(h => <th key={h} className="px-1 py-1.5 w-8">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.pitchers.map((p, i) => (
                <tr key={i} className="border-b border-[rgba(101,141,113,0.2)] last:border-0">
                  <td className="px-2 py-1.5 text-left">
                    <span className="text-[10px] font-black text-white uppercase tracking-wide">{p.name}</span>
                    <span className="ml-1.5 text-[8px] font-bold" style={{ color: p.side === 'away' ? '#a9c9b1' : '#c4333e' }}>{p.abbr}</span>
                  </td>
                  {[p.ip.toFixed(1), p.k, p.er, p.bb, p.h, p.p || '–'].map((v, j) => (
                    <td key={j} className={`px-1 py-1.5 text-[10px] tabular-nums font-bold ${j === 2 && p.er > 3 ? 'text-[#c4333e]' : 'text-[#a9c9b1]'}`}>{v}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PlayerPerformanceTrends;